import React, { useState } from 'react';

const MaintenanceAdmin = ({ API, adminToken, isDarkMode, showNotification, settings, setSettings, onSettingsUpdate }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const [showMaintenanceEnableModal, setShowMaintenanceEnableModal] = useState(false);
  const [showMaintenanceDisableModal, setShowMaintenanceDisableModal] = useState(false);

  const handleUpdateMaintenanceConfig = async (newConfig) => {
    try {
      // Update local state immediately for responsive UI
      setSettings(prev => ({
        ...prev,
        maintenanceConfig: newConfig
      }));

      // Send to backend
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({
          maintenanceConfig: newConfig
        })
      });

      if (response.ok) {
        // Also update main app settings
        if (onSettingsUpdate) {
          await onSettingsUpdate();
        }
        showNotification("Maintenance configuration updated", "success");
      } else {
        throw new Error("Failed to update maintenance config");
      }
    } catch (error) {
      console.error("Maintenance config error:", error);
      showNotification("Failed to update maintenance configuration", "error");
    }
  };

  return (
    <>
                <div className="space-y-6">
                  <div className={`p-8 rounded-xl border-2 ${
                    settings.maintenanceMode
                      ? 'border-red-500 bg-gradient-to-br from-red-900/30 to-orange-900/20'
                      : 'border-green-500 bg-gradient-to-br from-green-900/20 to-teal-900/10'
                  }`}>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          🚧 Maintenance Mode
                          {settings.maintenanceMode && (
                            <span className="text-sm px-3 py-1 bg-red-500 text-white rounded-full animate-pulse">
                              ACTIVE
                            </span>
                          )}
                        </h3>
                        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Control when regular users can access the application
                        </p>
                      </div>
                      <div className={`text-6xl ${settings.maintenanceMode ? 'animate-pulse' : ''}`}>
                        {settings.maintenanceMode ? '🔴' : '✅'}
                      </div>
                    </div>

                    {/* Current Status */}
                    <div className={`p-4 rounded-lg mb-6 ${
                      settings.maintenanceMode
                        ? isDarkMode ? 'bg-red-900/40' : 'bg-red-100'
                        : isDarkMode ? 'bg-green-900/40' : 'bg-green-100'
                    }`}>
                      <p className={`font-semibold ${
                        settings.maintenanceMode
                          ? isDarkMode ? 'text-red-300' : 'text-red-700'
                          : isDarkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        Current Status: {settings.maintenanceMode ? '🔴 ACTIVE' : '✅ Disabled'}
                      </p>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {settings.maintenanceMode
                          ? '⚠️ Regular users see maintenance page. Admins can still access and work.'
                          : '✓ App is accessible to all users.'}
                      </p>
                    </div>

                    {/* Control Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowMaintenanceEnableModal(true)}
                        className="flex-1 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-bold text-lg shadow-lg"
                      >
                        🔴 ENABLE MAINTENANCE MODE
                      </button>

                      <button
                        onClick={() => setShowMaintenanceDisableModal(true)}
                        className="flex-1 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-bold text-lg shadow-lg"
                      >
                        ✅ DISABLE (GO LIVE)
                      </button>
                    </div>

                    {/* Admin Access Info */}
                    <div className={`mt-6 p-4 rounded-lg border-2 ${
                      isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-300'
                    }`}>
                      <p className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        👨‍💼 Admin Access During Maintenance
                      </p>
                      <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>✓ You (admin) can see front and back</li>
                        <li>✓ You can access all admin features</li>
                        <li>✓ You can test and make changes</li>
                        <li>✓ Only regular user login is locked</li>
                        <li>⚠️ Regular users see maintenance page</li>
                      </ul>
                    </div>

                    {/* Customize Maintenance Page */}
                    <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        ✏️ Customize Maintenance Page
                      </p>

                      <div className="space-y-4">
                        {/* Main Message */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Main Message
                          </label>
                          <input
                            type="text"
                            value={settings.maintenanceConfig?.message || "We're currently performing scheduled maintenance"}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceConfig: {
                                  ...prev.maintenanceConfig,
                                  message: e.target.value
                                }
                              }));
                            }}
                            className={`w-full px-3 py-2 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                        </div>

                        {/* Details */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Details Message
                          </label>
                          <input
                            type="text"
                            value={settings.maintenanceConfig?.details || "We'll be back shortly. Thank you for your patience!"}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceConfig: {
                                  ...prev.maintenanceConfig,
                                  details: e.target.value
                                }
                              }));
                            }}
                            className={`w-full px-3 py-2 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                        </div>

                        {/* Save Button */}
                        <div className="pt-3 border-t border-gray-600">
                          <button
                            onClick={() => handleUpdateMaintenanceConfig(settings.maintenanceConfig)}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            💾 Save Page Settings
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Staff Access Settings */}
                    <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        🔐 Staff Access Settings
                      </p>

                      <div className="space-y-4">
                        {/* Enable Staff Access */}
                        <div className="flex items-center justify-between">
                          <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Enable Staff Access Login
                          </label>
                          <button
                            onClick={() => {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceConfig: {
                                  ...prev.maintenanceConfig,
                                  staffAccessEnabled: prev.maintenanceConfig?.staffAccessEnabled === false ? true : false
                                }
                              }));
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              settings.maintenanceConfig?.staffAccessEnabled !== false ? 'bg-green-600' : 'bg-gray-400'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.maintenanceConfig?.staffAccessEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>

                        {/* Staff Access Title */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Staff Access Title
                          </label>
                          <input
                            type="text"
                            value={settings.maintenanceConfig?.staffAccessTitle || "Staff Access"}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceConfig: {
                                  ...prev.maintenanceConfig,
                                  staffAccessTitle: e.target.value
                                }
                              }));
                            }}
                            className={`w-full px-3 py-2 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Staff Access"
                          />
                        </div>

                        {/* Enable CAPTCHA */}
                        <div className="flex items-center justify-between">
                          <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Enable CAPTCHA
                          </label>
                          <button
                            onClick={() => {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceConfig: {
                                  ...prev.maintenanceConfig,
                                  staffCaptchaEnabled: prev.maintenanceConfig?.staffCaptchaEnabled === false ? true : false
                                }
                              }));
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              settings.maintenanceConfig?.staffCaptchaEnabled !== false ? 'bg-green-600' : 'bg-gray-400'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.maintenanceConfig?.staffCaptchaEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>

                        {/* CAPTCHA Questions */}
                        {settings.maintenanceConfig?.staffCaptchaEnabled !== false && (
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              CAPTCHA Questions (one per line: question | answer)
                            </label>
                            <textarea
                              value={(settings.maintenanceConfig?.staffCaptchaQuestions || [
                                { question: "What color is the sky?", answer: "blue" },
                                { question: "How many days in a week?", answer: "7" },
                                { question: "2 + 2 = ?", answer: "4" }
                              ]).map(q => `${q.question} | ${q.answer}`).join('\n')}
                              onChange={(e) => {
                                const lines = e.target.value.split('\n');
                                const questions = lines.map(line => {
                                  const parts = line.split('|');
                                  if (parts.length >= 2) {
                                    return { question: parts[0].trim(), answer: parts[1].trim() };
                                  }
                                  return null;
                                }).filter(q => q && q.question && q.answer);
                                setSettings(prev => ({
                                  ...prev,
                                  maintenanceConfig: {
                                    ...prev.maintenanceConfig,
                                    staffCaptchaQuestions: questions.length > 0 ? questions : prev.maintenanceConfig?.staffCaptchaQuestions
                                  }
                                }));
                              }}
                              rows="4"
                              className={`w-full px-3 py-2 text-sm border rounded font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                              placeholder="What color is the sky? | blue
How many days in a week? | 7
2 + 2 = ? | 4"
                            />
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              Format: Question | Answer (answers are case-insensitive)
                            </p>
                          </div>
                        )}

                        {/* Save Button */}
                        <div className="pt-3 border-t border-gray-600">
                          <button
                            onClick={() => handleUpdateMaintenanceConfig(settings.maintenanceConfig)}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            💾 Save Staff Access Settings
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

      {/* Maintenance Enable Confirmation Modal */}
      {showMaintenanceEnableModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-70">
          <div className={`max-w-md w-full rounded-xl shadow-2xl p-6 ${
            isDarkMode ? 'bg-gray-800 border-2 border-red-700' : 'bg-white border-2 border-red-300'
          }`}>
            <div className="text-center">
              <div className="text-5xl mb-4">🚧</div>
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Maintenance Mode?
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Regular users will see the maintenance page, but you (admin) can still access and work on everything.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMaintenanceEnableModal(false)}
                  className="flex-1 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setShowMaintenanceEnableModal(false);
                    const response = await fetch(`${API}/admin/settings?maintenanceMode=true`, {
                      method: "PUT",
                      headers: { 'Authorization': `Bearer ${adminToken}` }
                    });

                    if (response.ok) {
                      const settingsRes = await fetch(`${API}/admin/settings`, {
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                      });
                      if (settingsRes.ok) {
                        const settingsData = await settingsRes.json();
                        setSettings(settingsData);
                      }
                      showNotification('🔴 Maintenance mode ENABLED! Users see maintenance page. You can still work.', 'success');
                    } else {
                      showNotification('Failed to enable', 'error');
                    }
                  }}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium"
                >
                  Yes, Enable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Disable Confirmation Modal */}
      {showMaintenanceDisableModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-70">
          <div className={`max-w-md w-full rounded-xl shadow-2xl p-6 ${
            isDarkMode ? 'bg-gray-800 border-2 border-green-700' : 'bg-white border-2 border-green-300'
          }`}>
            <div className="text-center">
              <div className="text-5xl mb-4">✅</div>
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Disable Maintenance Mode?
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                The app will be accessible to all users. Make sure everything is ready!
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMaintenanceDisableModal(false)}
                  className="flex-1 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setShowMaintenanceDisableModal(false);
                    const response = await fetch(`${API}/admin/settings?maintenanceMode=false`, {
                      method: "PUT",
                      headers: { 'Authorization': `Bearer ${adminToken}` }
                    });

                    if (response.ok) {
                      const settingsRes = await fetch(`${API}/admin/settings`, {
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                      });
                      if (settingsRes.ok) {
                        const settingsData = await settingsRes.json();
                        setSettings(settingsData);
                      }
                      showNotification('✅ Maintenance mode DISABLED! App is live.', 'success');
                    } else {
                      showNotification('Failed to disable', 'error');
                    }
                  }}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium"
                >
                  Yes, Go Live
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MaintenanceAdmin;
